import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowsLeftRight, Camera, CaretLeft, CircleNotch } from '@phosphor-icons/react'
import { Card } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { useAuth } from '@/core/AuthContext'
import {
  formatProgressPhotoDate,
  getComparePair,
  getProgressPhotos,
} from '../api/progressPhotos'
import type { ProgressPhoto } from '../api/progressPhotos'

export function CompareView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [photos, setPhotos] = useState<ProgressPhoto[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadPhotos = useCallback(async () => {
    if (!user) {
      setPhotos([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const loaded = await getProgressPhotos()
    setPhotos(loaded)
    setIsLoading(false)
  }, [user])

  useEffect(() => {
    void loadPhotos()
  }, [loadPhotos])

  if (!user) {
    return null
  }

  const comparePair = getComparePair(photos)

  return (
    <div className="min-h-screen">
      <div className="border-b border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900 safe-top">
        <div className="mx-auto max-w-6xl">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client/progress/photos')}
            className="mb-2"
          >
            <CaretLeft size={16} weight="bold" />
            Back to Gallery
          </Button>
          <Badge variant="info" size="sm" className="mb-1">
            Compare
          </Badge>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            First check-in vs latest
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-gray-200/70 bg-[linear-gradient(135deg,_rgba(255,255,255,0.94),_rgba(240,249,255,0.9))] p-6 shadow-xl shadow-sky-100/60 dark:border-white/10 dark:bg-[linear-gradient(135deg,_rgba(17,24,39,0.96),_rgba(2,6,23,0.96))] dark:shadow-none">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-600 dark:text-primary-400">
            Compare
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
            First check-in vs latest
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-slate-300">
            Use the same framing each time so visual changes are easier to read.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <CircleNotch size={32} weight="bold" className="animate-spin text-orange-400" />
          </div>
        ) : !comparePair ? (
          <Card className="rounded-[2rem] p-10 text-center">
            <div className="mx-auto flex max-w-md flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300">
                <ArrowsLeftRight size={30} weight="duotone" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Need at least two photos</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                  Capture another check-in and this comparison view will populate automatically.
                </p>
              </div>
              <Link to="/client/progress/photos/capture">
                <Button>
                  <Camera size={18} weight="bold" />
                  Take Progress Photo
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {[comparePair.first, comparePair.latest].map((photo, index) => (
              <Card key={photo.id} padding="none" className="overflow-hidden rounded-[2rem]">
                <div className="border-b border-gray-200/70 px-5 py-4 dark:border-white/10">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-slate-400">
                    {index === 0 ? 'Starting Point' : 'Latest Check-in'}
                  </p>
                  <h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                    {formatProgressPhotoDate(photo.createdAt)}
                  </h2>
                </div>
                <div className="aspect-[3/4] bg-black">
                  <img
                    src={photo.imageDataUrl}
                    alt={index === 0 ? 'First progress photo' : 'Latest progress photo'}
                    className="h-full w-full object-cover"
                  />
                </div>
              </Card>
            ))}
          </div>
        )}

      </div>
    </div>
  )
}
